import { Injectable, TransferState, makeStateKey } from '@angular/core';
import axios from 'axios';
import { BrandConfigurationService } from '../configuration/brand-configuration.service';
import { ScriptLoaderService } from '../script-loader/script-loader.service';
import type { PublicBusinessUnitsResponse, SkuProductReviewsResponse } from './trustpilot-response';

const BRAND_STARS_RATING = makeStateKey<PublicBusinessUnitsResponse>('brandStarsRating');
const PRODUCT_STARS_RATING = makeStateKey<SkuProductReviewsResponse>('productStarsRating');

const AXIOS_TIMEOUT_MS = 3_000;
const CACHE_TTL_MS = 5 * 60 * 1_000;
const CIRCUIT_BREAKER_THRESHOLD = 3;
const CIRCUIT_BREAKER_COOLDOWN_MS = 60 * 1_000;

interface CacheEntry<T> {
  data: T;
  expiresAt: number;
}

// Process-scoped state that persists across SSR renders within the same Node.js process.
const brandRatingCache = new Map<string, CacheEntry<PublicBusinessUnitsResponse>>();
const productRatingCache = new Map<string, CacheEntry<SkuProductReviewsResponse>>();
const brandRatingRequests = new Map<string, Promise<PublicBusinessUnitsResponse | null>>();
let consecutiveFailures = 0;
let circuitOpenUntil = 0;

/** @internal Reset module state between tests. */
export function _resetTrustpilotState(): void {
  brandRatingCache.clear();
  productRatingCache.clear();
  brandRatingRequests.clear();
  consecutiveFailures = 0;
  circuitOpenUntil = 0;
}

function isCircuitOpen(): boolean {
  if (consecutiveFailures >= CIRCUIT_BREAKER_THRESHOLD && Date.now() < circuitOpenUntil) {
    return true;
  }
  return false;
}

function recordSuccess(): void {
  consecutiveFailures = 0;
}

function recordFailure(): void {
  consecutiveFailures++;
  if (consecutiveFailures >= CIRCUIT_BREAKER_THRESHOLD) {
    circuitOpenUntil = Date.now() + CIRCUIT_BREAKER_COOLDOWN_MS;
  }
}

function getCached<T>(cache: Map<string, CacheEntry<T>>, key: string): T | null {
  const entry = cache.get(key);
  if (entry && Date.now() < entry.expiresAt) return entry.data;
  return null;
}

function getStale<T>(cache: Map<string, CacheEntry<T>>, key: string): T | null {
  return cache.get(key)?.data ?? null;
}

function setCache<T>(cache: Map<string, CacheEntry<T>>, key: string, data: T): void {
  cache.set(key, { data, expiresAt: Date.now() + CACHE_TTL_MS });
}

@Injectable({
  providedIn: 'root',
})
export class ServerTrustpilotService {
  private brandStarsRating: PublicBusinessUnitsResponse;
  private productStarsRating: SkuProductReviewsResponse;

  constructor(public sls: ScriptLoaderService, public bfs: BrandConfigurationService, public state: TransferState) {}

  async getBrandStarsRating(): Promise<PublicBusinessUnitsResponse | null> {
    this.brandStarsRating = this.state.get(BRAND_STARS_RATING, null);
    if (this.brandStarsRating) {
      return this.brandStarsRating;
    }

    const cacheKey = this.bfs.url || 'default';

    const cached = getCached(brandRatingCache, cacheKey);
    if (cached) {
      this.state.set(BRAND_STARS_RATING, cached);
      return cached;
    }

    if (isCircuitOpen()) {
      const stale = getStale(brandRatingCache, cacheKey);
      if (stale) this.state.set(BRAND_STARS_RATING, stale);
      return stale;
    }

    // Concurrent callers within a render share one upstream request. The circuit breaker therefore
    // counts one failure per upstream attempt, not per caller. TransferState is set per caller
    // because each SSR request has its own.
    let request = brandRatingRequests.get(cacheKey);
    if (request === undefined) {
      request = this.fetchBrandStarsRating(cacheKey).finally(() => brandRatingRequests.delete(cacheKey));
      brandRatingRequests.set(cacheKey, request);
    }

    const data = await request;
    if (data) this.state.set(BRAND_STARS_RATING, data);
    return data;
  }

  private async fetchBrandStarsRating(cacheKey: string): Promise<PublicBusinessUnitsResponse | null> {
    try {
      const headers = {
        'Content-Type': 'application/json',
        apikey: this.bfs.trustpilotApiKey,
      };
      const baseURL = 'https://api.trustpilot.com/v1/business-units/find';
      const response = await axios.get(baseURL, {
        headers,
        params: { name: this.bfs.url },
        timeout: AXIOS_TIMEOUT_MS,
      });
      const data = response.data as PublicBusinessUnitsResponse;
      recordSuccess();
      setCache(brandRatingCache, cacheKey, data);
      return data;
    } catch (error) {
      recordFailure();
      // eslint-disable-next-line no-console
      console.warn('Trustpilot API unavailable (brand rating):', (error as Error)?.message);
      return getStale(brandRatingCache, cacheKey);
    }
  }

  async getProductStarsRating(sku: string): Promise<SkuProductReviewsResponse | null> {
    this.productStarsRating = this.state.get(PRODUCT_STARS_RATING, null);
    if (this.productStarsRating) {
      return this.productStarsRating;
    }

    const cacheKey = `${this.bfs.trustpilotId}_${sku}`;

    const cached = getCached(productRatingCache, cacheKey);
    if (cached) {
      this.state.set(PRODUCT_STARS_RATING, cached);
      return cached;
    }

    if (isCircuitOpen()) {
      const stale = getStale(productRatingCache, cacheKey);
      if (stale) this.state.set(PRODUCT_STARS_RATING, stale);
      return stale;
    }

    try {
      const headers = {
        'Content-Type': 'application/json',
        apikey: this.bfs.trustpilotApiKey,
      };
      const baseURL = 'https://api.trustpilot.com/v1/product-reviews/business-units/' + this.bfs.trustpilotId;
      const response = await axios.get(baseURL, {
        headers,
        params: { sku },
        timeout: AXIOS_TIMEOUT_MS,
      });
      const data = response.data as SkuProductReviewsResponse;
      recordSuccess();
      setCache(productRatingCache, cacheKey, data);
      this.state.set(PRODUCT_STARS_RATING, data);
      return data;
    } catch (error) {
      recordFailure();
      // eslint-disable-next-line no-console
      console.warn('Trustpilot API unavailable (product rating):', (error as Error)?.message);
      const stale = getStale(productRatingCache, cacheKey);
      if (stale) this.state.set(PRODUCT_STARS_RATING, stale);
      return stale;
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars, @typescript-eslint/no-explicit-any
  loadTrustpilot = (_container: unknown): void => {
    return;
  };
}
